import type { ReactNode } from 'react';
import { EmptyState, ErrorState, LoadingState } from '../components/states.js';
import { StatusDot, type Tone } from '../components/ui.js';
import type { Dostupnost, Integracia } from '../api/client.js';
import type { Nacitanie } from '../api/use-api.js';
import { useSession } from '../auth/session.js';

/*
 * Spolocne casti obrazoviek.
 *
 * `Data` vynucuje, ze ziadna obrazovka nezabudne na niektory z troch
 * povinnych stavov: nacitavanie, chyba, prazdny zoznam.
 */

export function Data<T>({
  nacitanie,
  prazdne,
  children,
}: {
  readonly nacitanie: Nacitanie<T>;
  readonly prazdne?: { readonly title: string; readonly detail: string };
  readonly children: (data: T) => ReactNode;
}): ReactNode {
  const { vyprsala } = useSession();

  if (nacitanie.needsLogin) {
    // Relacia vyprsala; obal prepne na prihlasenie namiesto prazdnej stranky.
    vyprsala();
    return <LoadingState what="Presmerúvam na prihlásenie" />;
  }
  if (nacitanie.loading) return <LoadingState />;
  if (nacitanie.error !== undefined) {
    return <ErrorState detail={nacitanie.error} onRetry={nacitanie.reload} />;
  }
  if (!nacitanie.data) {
    return (
      <EmptyState
        title={prazdne?.title ?? 'Žiadne dáta'}
        detail={prazdne?.detail ?? 'Zatiaľ tu nie je čo zobraziť.'}
      />
    );
  }
  return children(nacitanie.data);
}

// --- prevody stavov na vzhlad ---------------------------------------------

export const TON_DOSTUPNOSTI: Record<Dostupnost['state'], Tone> = {
  online: 'ok',
  offline: 'danger',
  unknown: 'neutral',
};

export const POPIS_DOSTUPNOSTI: Record<Dostupnost['state'], string> = {
  online: 'Dostupné',
  offline: 'Nedostupné',
  unknown: 'Neznámy stav',
};

/**
 * Batériové zariadenie, ktoré spí, NIE JE pokazené.
 *
 * Most ho hlási ako nedostupné, lebo mu neodpovedalo na dotaz — PIR
 * a tlačidlá väčšinu času spia. Vysielať vedia a hodnoty, ktoré poslali,
 * platia. „Nedostupné“ by z toho robilo poruchu.
 */
const SPI = 'asleep';

export function popisDostupnosti(availability: Dostupnost): string {
  if (availability.state === 'offline' && availability.reason === SPI) return 'Spí';
  return POPIS_DOSTUPNOSTI[availability.state];
}

export function tonDostupnosti(availability: Dostupnost): Tone {
  if (availability.state === 'offline' && availability.reason === SPI) return 'neutral';
  return TON_DOSTUPNOSTI[availability.state];
}

export const TON_INTEGRACIE: Record<Integracia['state'], Tone> = {
  running: 'ok',
  degraded: 'warn',
  failed: 'danger',
  stopped: 'neutral',
  starting: 'neutral',
};

export const POPIS_INTEGRACIE: Record<Integracia['state'], string> = {
  running: 'Beží',
  degraded: 'Čiastočne',
  failed: 'Zlyhala',
  stopped: 'Zastavená',
  starting: 'Spúšťa sa',
};

/** Nazov schopnosti tak, ako sa hovori po slovensky. */
export const NAZOV_SCHOPNOSTI: Record<string, string> = {
  OnOff: 'Zapnutie',
  Brightness: 'Jas',
  ColorTemperature: 'Teplota bielej',
  Color: 'Farba',
  Position: 'Poloha',
  Contact: 'Kontakt',
  Temperature: 'Teplota',
  Humidity: 'Vlhkosť',
  Occupancy: 'Prítomnosť',
  Illuminance: 'Osvetlenie',
  AirQuality: 'Kvalita vzduchu',
  Co2: 'CO2',
  Pm25: 'Prachové častice',
  Battery: 'Batéria',
  Power: 'Výkon',
  Energy: 'Spotreba',
  Voltage: 'Napätie',
  Current: 'Prúd',
  PowerFactor: 'Účinník',
  Action: 'Tlačidlo',
  Pressure: 'Tlak',
  WindSpeed: 'Vietor',
  WindGust: 'Nárazy vetra',
  WindDirection: 'Smer vetra',
  CloudCover: 'Oblačnosť',
  Irradiance: 'Slnečné žiarenie',
  UvIndex: 'UV index',
  Precipitation: 'Zrážky',
  PrecipitationProbability: 'Pravdepodobnosť zrážok',
  DewPoint: 'Rosný bod',
  WeatherCode: 'Stav oblohy',
};

export const nazovSchopnosti = (name: string): string => NAZOV_SCHOPNOSTI[name] ?? name;

/** Farba textu podla tonu; bodka sama je v zozname primalo. */
export function farbaTonu(ton: 'ok' | 'warn' | 'danger' | 'neutral' | 'accent'): string {
  if (ton === 'ok') return 'text-(--color-ok)';
  if (ton === 'warn') return 'text-(--color-warn)';
  if (ton === 'danger') return 'text-(--color-danger)';
  if (ton === 'accent') return 'text-(--color-accent)';
  return 'text-(--color-text-muted)';
}

export function StavZariadenia({
  availability,
  lastMessageAt,
}: {
  readonly availability: Dostupnost;
  /** Kedy prišla posledná hodnota; ukazuje sa pri každom stave. */
  readonly lastMessageAt?: string | null;
}): ReactNode {
  return (
    <span
      /*
       * Zalamovat sa MUSI: na mobile je „Nedostupné · 17. 9. 2026
       * 15:57:10" sirsie nez dlazdica a nezalomeny `inline-flex` ju
       * pretekal (hlasene 2026-09-17).
       */
      className="inline-flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-(--color-text-muted)"
      title={
        availability.state === 'offline' && availability.reason === SPI
          ? 'Most ho nedosiahne — batériové zariadenie väčšinu času spí. Hodnoty, ktoré samo poslalo, platia.'
          : availability.since
            ? `Stav trvá od ${new Date(availability.since).toLocaleString('sk-SK')}`
            : undefined
      }
    >
      <StatusDot tone={tonDostupnosti(availability)} />
      {/*
        Stav nesie FARBU, nielen bodku: v zozname sa hlada ocami a sivy
        text pri farebnej bodke splynie (hlasene 2026-09-18).
      */}
      <span className={farbaTonu(tonDostupnosti(availability))}>
        {popisDostupnosti(availability)}
      </span>
      {lastMessageAt !== undefined && (
        <span className="text-(--color-text-subtle)">
          ·{' '}
          {lastMessageAt === null
            ? 'nič neposlalo'
            : new Date(lastMessageAt).toLocaleString('sk-SK')}
        </span>
      )}
    </span>
  );
}
